import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

/**
 * Media is typed by WHAT it is, never by where it is hosted. Fixed display order.
 * `group` drives the Videos/Images tabs in ProjectQuickLook. A walkthrough link may be
 * a single video or a playlist — ProjectQuickLook expands playlists into their videos.
 */
const MEDIA_KINDS = [
  { kind: "walkthrough", label: "Walkthrough", group: "videos" },
  { kind: "demo", label: "Demo", group: "videos" },
  { kind: "site", label: "Site video", group: "videos" },
  { kind: "renders", label: "Renders", group: "images" },
  { kind: "photos", label: "Site images", group: "images" },
];

export function dbToProject(row) {
  const media = Array.isArray(row.media) ? row.media : [];
  const urlsOf = (kind) => media.filter((m) => m.kind === kind).map((m) => m.url);
  return {
    id: row.id,
    name: row.name || "",
    surfaces: row.surfaces || [],
    currentBuildId: row.current_build_id || null,
    buildKey: row.build_key || "",
    buildLabel: row.build_label || "",
    // Every build the project has had, newest first (projects_view.builds).
    builds: (Array.isArray(row.builds) ? row.builds : []).map((b) => ({
      id: b.id,
      key: b.key,
      label: b.label,
      status: b.status,
      streampixelAppId: b.streampixel_app_id,
      server: b.server,
      mayaEnabled: b.maya_enabled,
      streampixelOff: b.streampixel_off,
      retiredAt: b.retired_at,
      createdAt: b.created_at,
    })),
    server: row.server || "",
    sbu: row.sbu || "",
    constructionType: row.construction_type || "",
    industry: row.industry || "",
    designStyle: row.design_style || "",
    areaSqft: row.area_sqft ?? null,
    city: row.city || "",
    country: row.country || "",
    description: row.description || "",
    vizdomId: row.vizdom_project_id || "",
    thumbnail: row.thumbnail_url || urlsOf("renders")[0] || "",
    vr: urlsOf("vr"),
    media: MEDIA_KINDS
      .map(({ kind, label, group }) => ({ kind, label, group, urls: urlsOf(kind) }))
      .filter((m) => m.urls.length > 0),
    hasBuild: !!row.current_build_id,
  };
}

/**
 * The ONLY function in the app that knows where project data comes from.
 * RLS already scopes rows per user; `published` and `archived_at` hide drafts
 * and archived projects from internal users too.
 */
export async function fetchProjects() {
  // Same tie-break as the admin list's "Gallery order" (listProjects in
  // features/admin/api.js): two rows sharing a display_order — a fresh
  // duplicate sits on its original's value on purpose — land in the same
  // relative order visitors and admins both see, oldest first.
  const { data, error } = await supabase
    .from("projects_view")
    .select("*")
    .eq("published", true)
    .is("archived_at", null)
    .order("display_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(dbToProject);
}

/** @param {string} [surface] "Discover" | "Showcase" | "Live" — omit for the whole catalog. */
export function useProjects(surface) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProjects()
      .then((all) => {
        if (cancelled) return;
        setProjects(surface ? all.filter((p) => p.surfaces.includes(surface)) : all);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Failed to load projects:", e);
        setError(e);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [surface, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { projects, loading, error, reload };
}
